import { FormField, OptionGroup } from '@dev-spendesk/grapes';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';

import { useTranslation } from 'common/hooks/useTranslation';
import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { useGetDefaultExpenseAccountQuery } from 'modules/bookkeep/settings/integrations/hooks/useGetDefaultExpenseAccountQuery';
import { BulkImportExpenseAccountForm } from 'modules/bookkeep/settings/integrations/pages/LegacyIntegrationsAccountingPage/sections/ExpenseAccountsSection/BulkImportExpenseAccountForm';
import { ExpenseAccountErrorModal } from 'modules/bookkeep/settings/integrations/pages/LegacyIntegrationsAccountingPage/sections/ExpenseAccountsSection/ExpenseAccountErrorModal';
import {
  reshapeToFormData,
  useExpenseAccountsMutation,
} from 'modules/bookkeep/settings/integrations/pages/LegacyIntegrationsAccountingPage/sections/ExpenseAccountsSection/useImportExpenseAccountsMutation';
import { TaskStepLayout } from 'modules/onboarding/components/TaskStepLayout';
import { useUpdateDefaultExpenseAccount } from 'modules/onboarding/setup-hub/pages/Accounting/AccountingExpenseAccounts/hooks/useUpdateDefaultExpenseAccount';
import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { routeFor } from 'src/core/constants/routes';

import { type FormValues } from './form';
import { useUpdateCachedTaskStatus } from '../../SetupHubPage/hooks/useUpdateCachedTaskStatus';
import { routes } from '../../routes';
import { ExpenseAccountTableAndModals } from '../components/ExpenseAccountTableAndModals';

export const AccountingExpenseAccounts = () => {
  const history = useHistory();

  const { t } = useTranslation('global');

  const companyId = useCompanyId();

  const [
    importExpenseAccounts,
    importExpenseAccountsState,
    resetImportExpenseAccountsState,
  ] = useExpenseAccountsMutation();
  const importErrors =
    importExpenseAccountsState.status === 'error' &&
    importExpenseAccountsState.error.type === 'RequestError'
      ? importExpenseAccountsState.error.data.errors
      : [];

  const defaultExpenseAccountQueryState = useGetDefaultExpenseAccountQuery();
  const defaultExpenseAccount = unwrapQuery(defaultExpenseAccountQueryState);
  const updateDefaultExpenseAccount = useUpdateDefaultExpenseAccount();

  const updateCachedTaskStatus = useUpdateCachedTaskStatus();

  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    setFieldValue,
    setFieldError,
    submitCount,
  } = useFormik<FormValues>({
    initialValues: {
      file: undefined,
      prefixNameWithCode: true,
      useDefaultValue:
        !defaultExpenseAccount?.isArchived && !!defaultExpenseAccount?.id,
      defaultExpenseAccountCode: defaultExpenseAccount?.code ?? '',
      tab: 'import',
    },
    enableReinitialize: true,
    validate: (newValues) => {
      if (newValues.tab === 'import' && !newValues.file) {
        return { file: 'missing_file' };
      }
      if (
        newValues.tab === 'manual' &&
        newValues.useDefaultValue &&
        !newValues.defaultExpenseAccountCode?.trim()?.length
      ) {
        return { defaultExpenseAccountCode: t('misc.requiredField') };
      }
    },
    onSubmit: async (newValues) => {
      if (newValues.tab === 'import') {
        if (!newValues.file) {
          return;
        }
        const payload = reshapeToFormData(
          newValues.file,
          newValues.prefixNameWithCode,
        );
        try {
          await importExpenseAccounts(payload);

          const nextPath = routeFor(
            routes.ACCOUNTING_EXPENSE_ACCOUNTS_LIST.path,
            { company: companyId },
          );
          history.push(nextPath);
        } catch {
          // Not doing anything here
          // because errors are handled through the mutation state
        }
      } else if (newValues.tab === 'manual') {
        await updateDefaultExpenseAccount(defaultExpenseAccount, values, () => {
          updateCachedTaskStatus('expense_accounts_codes', 'setup_hub');
          goBackToHub();
        });
      }
    },
  });

  const goBackToHub = () => {
    const setupHubPath = routeFor(routes.SETUP_HUB_TASK_LIST.path, {
      company: companyId,
    });
    history.push(setupHubPath);
  };

  return (
    <div className="page__container">
      <TaskStepLayout
        description={t('setupHub.expenseAccounts.description')}
        extraDescription={
          <p className="text-content-secondary-bg-primary body-l">
            {t('setupHub.expenseAccounts.extraDescription')}
          </p>
        }
        onBack={goBackToHub}
        onNext={handleSubmit}
        title={t('setupHub.expenseAccounts.title')}
        video={{
          // TODO(GROW-1484): Replace with localized video URL
          url: '',
          title: t('setupHub.expenseAccounts.helpTitle'),
        }}
      >
        <FormField
          className="mb-16"
          label={t('setupHub.expenseAccounts.tabs.label')}
        >
          <OptionGroup
            name="tab"
            options={[
              {
                value: 'import',
                label: t('setupHub.expenseAccounts.tabs.importOption'),
              },
              {
                value: 'manual',
                label: t('setupHub.expenseAccounts.tabs.manualOption'),
              },
            ]}
            value={values.tab}
            onChange={(event) =>
              setFieldValue(event.target.name, event.target.value)
            }
          />
        </FormField>
        {values.tab === 'import' && (
          <>
            <div className="rounded-8 bg-background-secondary-default px-24 py-16">
              <BulkImportExpenseAccountForm
                templateButtonVariant="secondaryNeutral"
                file={values.file}
                hasMissingFileError={submitCount > 0 && !!errors.file}
                prefixNameWithCode={values.prefixNameWithCode}
                setFile={(file) => setFieldValue('file', file)}
                setHasMissingFileError={(hasError) =>
                  setFieldError('file', hasError ? 'missing_file' : undefined)
                }
                setPrefixNameWithCode={(prefixNameWithCode) =>
                  setFieldValue('prefixNameWithCode', prefixNameWithCode)
                }
              />
            </div>
            <ExpenseAccountErrorModal
              action={resetImportExpenseAccountsState}
              errors={importErrors}
              isOpen={importErrors.length > 0}
            />
          </>
        )}
        {values.tab === 'manual' && (
          <ExpenseAccountTableAndModals
            values={values}
            handleChange={handleChange}
            errors={errors}
            submitCount={submitCount}
          />
        )}
      </TaskStepLayout>
    </div>
  );
};
